import { useCallback } from "preact/hooks";
import { useSignal, useSignalEffect } from "@preact/signals";
import { useBreakpoint } from "~/hooks";
import { Autocomplete } from "../Autocomplete";
import { Dialog } from "../Dialog";
import { IconButton } from "../IconButton";
import { MobileDropdown } from "../MobileDropdown";
import Popup from "./Popup";
import { BaseChoice, RichAutocompleteProps } from "./interfaces";
import SearchIcon from "@material-design-icons/svg/outlined/search.svg";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const RichAutocomplete = <Row extends BaseChoice>({
    header,
    rowHeight,
    renderRow,
    completeRich,
    loading,
    actions,
    className,
    defaultWidth,
    minWidth,
    noResize,
    dropdownChoices,
    dropdownPlaceholder,
    isExpanded: extIsExpanded,
    hideInput,
    zIndex,
    storageKey,
    sort,
    sortGroup,
    label,
    ...autocompleteProps
}: RichAutocompleteProps<Row>) => {
    const isExpanded = useSignal(extIsExpanded?.peek() ?? false);
    const defaultRichQuery = useSignal("");
    const bpMd = useBreakpoint("md");

    useSignalEffect(() => {
        if (!isExpanded.value) {
            defaultRichQuery.value = "";
        }
    });

    useSignalEffect(() => {
        if (!extIsExpanded) {
            return;
        }

        isExpanded.value = extIsExpanded.value;
    });

    const handlePopupClose = useCallback(() => {
        isExpanded.value = false;

        if (extIsExpanded) {
            extIsExpanded.value = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extIsExpanded]);

    return (
        <div
            className={cn(
                "flex flex-col gap-y-1",
                autocompleteProps.variant === "flat" && "h-full",
            )}
        >
            {label && <span className="text-sm text-gray-500">{label}</span>}
            <div className={cn("flex w-full items-center", className)}>
                <Dialog
                    show={isExpanded}
                    centered={bpMd.value}
                    onClose={handlePopupClose}
                    zIndex={zIndex}
                >
                    <Popup
                        header={header}
                        rowHeight={rowHeight}
                        renderRow={renderRow}
                        loading={loading}
                        complete={completeRich}
                        value={autocompleteProps.value}
                        defaultQuery={defaultRichQuery.value}
                        onChange={autocompleteProps.onChange}
                        onClose={handlePopupClose}
                        actions={actions}
                        defaultWidth={defaultWidth}
                        minWidth={minWidth}
                        noResize={noResize}
                        storageKey={storageKey}
                        sort={sort}
                        sortGroup={sortGroup}
                    />
                </Dialog>
                {!hideInput && (
                    <>
                        {!bpMd.value && dropdownChoices ? (
                            <MobileDropdown
                                choices={dropdownChoices.value}
                                value={autocompleteProps.value}
                                onChange={autocompleteProps.onChange}
                                onExpand={() => (isExpanded.value = true)}
                                placeholder={dropdownPlaceholder}
                                renderLabel={autocompleteProps.renderLabel}
                                renderOption={autocompleteProps.renderOption}
                                maxSuggestions={
                                    autocompleteProps.maxSuggestions
                                }
                                disabled={autocompleteProps.disabled}
                                className="mr-2"
                                inputClassName={
                                    autocompleteProps.inputClassName
                                }
                                variant={autocompleteProps.variant}
                            />
                        ) : (
                            <Autocomplete
                                className="mr-2 w-full"
                                onMore={(q) => {
                                    isExpanded.value = true;
                                    defaultRichQuery.value = q;
                                }}
                                {...autocompleteProps}
                            />
                        )}
                        <IconButton
                            flat
                            title="Expand search"
                            disabled={autocompleteProps.disabled}
                            onClick={() => (isExpanded.value = true)}
                            className={
                                autocompleteProps.variant === "flat"
                                    ? "mr-2"
                                    : ""
                            }
                        >
                            {loading ? (
                                <Loader width="18" height="18" />
                            ) : (
                                <SearchIcon width="18" height="18" />
                            )}
                        </IconButton>
                    </>
                )}
            </div>
        </div>
    );
};

export default RichAutocomplete;
